import { peekBranding } from "@/lib/branding";
import { useCallback, useEffect, useState } from "react";
import { Megaphone, Send, Bell, Smartphone, Mail, ChevronDown, ChevronUp, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useConfirm } from "@/hooks/useConfirm";
import { parseMemo } from "@/components/dashboard/NotificationBell";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// superadmin is deliberately NOT a choice — superadmins are treated as
// admins and receive admin broadcasts automatically (but stay unmentioned).
const ROLE_CHOICES = [
  { key: "owner", label: "Owners" },
  { key: "admin", label: "Admins" },
  { key: "manager", label: "Managers" },
  { key: "employee", label: "Employees" },
  { key: "customer", label: "Customers" },
  { key: "new_user", label: "New users" },
];

/**
 * Broadcast tab (home section, owners/admins only): write a message, tick the
 * roles to reach, send. Every recipient gets an in-app notification + phone
 * alert, and a copy emailed to their account email.
 */
const BroadcastTab = () => {
  const confirm = useConfirm();
  const [message, setMessage] = useState("");
  const [roles, setRoles] = useState(() => new Set(["employee"]));
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [expanded, setExpanded] = useState(null); // broadcast_id
  const [people, setPeople] = useState([]);       // all users for the picker
  const [picked, setPicked] = useState(() => new Set()); // hand-picked user_ids
  const [peopleQ, setPeopleQ] = useState("");
  const [viewMsg, setViewMsg] = useState(null);   // broadcast message modal

  const loadLog = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/broadcast-log");
      setLog(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications/recipients");
        setPeople(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
  }, []);

  const togglePerson = (uid) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  // Delete a broadcast from the log AND from every recipient's notifications.
  const deleteBroadcast = async (b) => {
    const ok = await confirm({
      title: "Delete this broadcast?",
      description: `"${(b.message || "").slice(0, 80)}" disappears from the delivery log AND from every recipient's notifications.`,
      confirmLabel: "Delete broadcast",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/notifications/broadcast/${b.broadcast_id}`);
      toast.success(`Broadcast deleted (${data.removed} notification${data.removed === 1 ? "" : "s"} removed)`);
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleRole = (key) => {
    setRoles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const send = async () => {
    if (!message.trim()) { toast.error("Write a message first"); return; }
    if (roles.size === 0 && picked.size === 0) { toast.error("Pick at least one role or person"); return; }
    const parts = [];
    const roleNames = ROLE_CHOICES.filter((r) => roles.has(r.key)).map((r) => r.label.toLowerCase());
    if (roleNames.length) parts.push(`every ${roleNames.join(", ")} account`);
    if (picked.size) parts.push(`${picked.size} hand-picked ${picked.size === 1 ? "person" : "people"}`);
    const ok = await confirm({
      title: "Send this broadcast?",
      description: `Goes to ${parts.join(" + ")} — in-app notification, phone alert, and an email copy.`,
      confirmLabel: "Send broadcast",
    });
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await api.post("/notifications/broadcast", { message: message.trim(), roles: [...roles], user_ids: [...picked] });
      const mailNote = data.emailed > 0
        ? `, ${data.emailed} emailed${data.email_failed ? ` (${data.email_failed} failed)` : ""}`
        : " (no mailbox connected — email copies skipped)";
      toast.success(`Broadcast sent to ${data.recipients} user${data.recipients === 1 ? "" : "s"}${mailNote}`);
      setMessage("");
      setPicked(new Set());
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4 sm:p-6" data-testid="broadcast-tab">
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-[#8fa4f0]" />
          <h2 className="text-lg font-bold uppercase tracking-tight text-white">Broadcast</h2>
        </div>
        <p className="mt-1 text-xs text-white/50">
          Sends an in-app notification + phone alert to everyone in the selected roles, and emails a copy to their account email.
        </p>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="Type the message…"
          data-testid="broadcast-message"
          className="mt-4 w-full rounded-md border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#1a3fd1] focus:outline-none"
        />

        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/50">Send to</p>
        <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="broadcast-roles">
          {ROLE_CHOICES.map((r) => (
            <label
              key={r.key}
              className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                roles.has(r.key)
                  ? "border-[#1a3fd1] bg-[#1a3fd1]/15 text-white"
                  : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
              }`}
              data-testid={`broadcast-role-${r.key}`}
            >
              <input
                type="checkbox"
                checked={roles.has(r.key)}
                onChange={() => toggleRole(r.key)}
                className="h-3.5 w-3.5 accent-[#1a3fd1]"
              />
              {r.label}
            </label>
          ))}
        </div>

        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/50">Or specific people</p>
        <input
          value={peopleQ}
          onChange={(e) => setPeopleQ(e.target.value)}
          placeholder="Search people…"
          data-testid="broadcast-people-search"
          className="mt-1.5 w-full rounded-md border border-white/15 bg-white/[0.03] px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:border-[#1a3fd1] focus:outline-none"
        />
        <div className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-md border border-white/10 p-2" data-testid="broadcast-people">
          {people
            .filter((p) => p.role !== "superadmin") // stays unmentioned; gets admin broadcasts anyway
            .filter((p) => {
              const t = peopleQ.trim().toLowerCase();
              return !t || (p.name || "").toLowerCase().includes(t) || (p.email || "").toLowerCase().includes(t);
            })
            .map((p) => (
              <label
                key={p.user_id}
                className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                  picked.has(p.user_id) ? "bg-[#1a3fd1]/15 text-white" : "text-white/70 hover:bg-white/5"
                }`}
                data-testid={`broadcast-person-${p.user_id}`}
              >
                <input
                  type="checkbox"
                  checked={picked.has(p.user_id)}
                  onChange={() => togglePerson(p.user_id)}
                  className="h-3.5 w-3.5 accent-[#1a3fd1]"
                />
                <span className="min-w-0 flex-1 truncate">{p.name || p.email}</span>
                {p.active === false && <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-amber-400/80">inactive</span>}
                <span className="shrink-0 font-mono text-[10px] text-white/35">{p.role}</span>
              </label>
            ))}
          {people.length === 0 && <p className="py-3 text-center text-xs text-white/40">Loading people…</p>}
        </div>

        <button
          type="button"
          onClick={send}
          disabled={sending}
          data-testid="broadcast-send"
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#2ca01c] px-4 py-2 text-sm font-semibold text-white hover:bg-[#248015] disabled:opacity-50"
        >
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send broadcast"}
        </button>
      </div>

      {/* Delivery log — confirm everyone actually got it */}
      {log.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 sm:p-5" data-testid="broadcast-log">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Delivery log</p>
          <div className="mt-3 space-y-2">
            {log.map((b, i) => {
              // Superadmin stays invisible in the log (treated as an admin
              // for delivery, but never mentioned on screen).
              const recips = (b.recipients || []).filter((r) => r.role !== "superadmin");
              const pushed = recips.filter((r) => (r.pushed || 0) > 0).length;
              const emailed = recips.filter((r) => r.emailed).length;
              const open = expanded === b.broadcast_id;
              return (
                <div key={b.broadcast_id} className="rounded-md border border-white/10 bg-white/[0.02]" data-testid={`broadcast-log-entry-${i}`}>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setViewMsg(b)}
                      data-testid={`broadcast-log-view-${i}`}
                      title="Open the sent message"
                      className="min-w-0 flex-1 px-3 py-2.5 text-left"
                    >
                      <span className="block truncate text-sm text-white hover:underline">{parseMemo(b.message).subject || b.message}</span>
                      <span className="mt-0.5 block font-mono text-[10px] text-white/40">
                        {b.sender_name} · {timeAgo(b.created_at)} · {recips.length} notified · {pushed} pushed · {emailed} emailed
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpanded(open ? null : b.broadcast_id)}
                      data-testid={`broadcast-log-toggle-${i}`}
                      aria-label={open ? "Hide recipients" : "Show recipients"}
                      className="shrink-0 rounded p-1.5 text-white/40 hover:text-white"
                    >
                      {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteBroadcast(b)}
                      data-testid={`broadcast-log-delete-${i}`}
                      aria-label="Delete broadcast"
                      title="Delete — also removes it from every recipient's notifications"
                      className="mr-2 shrink-0 rounded p-1.5 text-white/30 hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {open && (
                    <ul className="border-t border-white/10 px-3 py-2" data-testid={`broadcast-log-detail-${i}`}>
                      {recips.map((r) => (
                        <li key={r.user_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`broadcast-log-recip-${i}-${r.user_id}`}>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-white/80">{r.name || r.email || r.user_id}</span>
                            <span className="block font-mono text-[10px] text-white/35">{r.role}{r.name && r.email ? ` · ${r.email}` : ""}</span>
                          </span>
                          <span className="inline-flex items-center gap-1 text-[#8fa4f0]" title="In-app notification">
                            <Bell className="h-3 w-3" /><Check className="h-3 w-3 text-emerald-300" />
                          </span>
                          <span className={`inline-flex items-center gap-1 ${(r.pushed || 0) > 0 ? "text-emerald-300" : "text-white/25"}`} title={(r.pushed || 0) > 0 ? `Pushed to ${r.pushed} device${r.pushed === 1 ? "" : "s"}` : "No device subscribed"}>
                            <Smartphone className="h-3 w-3" />{(r.pushed || 0) > 0 ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          </span>
                          <span className={`inline-flex items-center gap-1 ${r.emailed ? "text-emerald-300" : "text-white/25"}`} title={r.emailed ? "Email copy sent" : "Email not sent"}>
                            <Mail className="h-3 w-3" />{r.emailed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Sent-message preview modal — click any log entry's message text */}
      {viewMsg && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={() => setViewMsg(null)} data-testid="broadcast-message-modal">
          <div
            className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
              <img src="/api/branding/icon/elite-logo-dark.png" alt="" className="logo-dark-only h-8 w-auto object-contain" />
              <img src="/api/branding/icon/elite-logo-light.png" alt="" className="logo-light-only h-8 w-auto object-contain" />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8fa4f0]">{peekBranding()?.app_name || "Elite Mechanical"}</p>
                <p className="truncate text-sm font-semibold text-white" data-testid="broadcast-message-from">
                  From {viewMsg.sender_name || "System"}
                </p>
              </div>
              <button type="button" onClick={() => setViewMsg(null)} aria-label="Close" data-testid="broadcast-message-close" className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              {parseMemo(viewMsg.message).subject && (
                <h3 className="text-base font-semibold text-white">{parseMemo(viewMsg.message).subject}</h3>
              )}
              <p className={`whitespace-pre-wrap text-sm leading-relaxed text-white/80 ${parseMemo(viewMsg.message).subject ? "mt-2" : ""}`} data-testid="broadcast-message-body">{parseMemo(viewMsg.message).body}</p>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-white/40">
                {timeAgo(viewMsg.created_at)} · {(viewMsg.recipients || []).filter((r) => r.role !== "superadmin").length} recipient{(viewMsg.recipients || []).filter((r) => r.role !== "superadmin").length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastTab;
